import { useCallback } from 'react';
import useSWR from 'swr';
import { apiClient, ApiClientError } from '@/api';
import { useAuth } from './useAuth';

export interface OnboardingStatus {
  show_onboarding: boolean;
  completed_at: string | null;
  skipped_at: string | null;
}

export interface OnboardingCompleteResponse {
  completed_at: string;
}

export interface OnboardingSkipResponse {
  skipped_at: string;
}

export interface UseOnboardingResult {
  status: OnboardingStatus | undefined;
  isLoading: boolean;
  error: ApiClientError | null;
  showOnboarding: boolean;
  completeOnboarding: () => Promise<OnboardingCompleteResponse>;
  skipOnboarding: () => Promise<OnboardingSkipResponse>;
  resetOnboarding: () => Promise<void>;
  refresh: () => void;
}

export function useOnboarding(): UseOnboardingResult {
  const { session } = useAuth();

  const fetcher = async (url: string): Promise<OnboardingStatus> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    return apiClient.get<OnboardingStatus>(url, { token: session.access_token });
  };

  const { data, error, isLoading, mutate } = useSWR<OnboardingStatus, ApiClientError>(
    session?.access_token ? '/api/v1/me/onboarding' : null,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    }
  );

  const completeOnboarding = useCallback(async (): Promise<OnboardingCompleteResponse> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    const response = await apiClient.post<OnboardingCompleteResponse>(
      '/api/v1/me/onboarding/complete',
      {},
      { token: session.access_token }
    );
    mutate({ show_onboarding: false, completed_at: response.completed_at, skipped_at: null });
    return response;
  }, [session?.access_token, mutate]);

  const skipOnboarding = useCallback(async (): Promise<OnboardingSkipResponse> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    const response = await apiClient.post<OnboardingSkipResponse>(
      '/api/v1/me/onboarding/skip',
      {},
      { token: session.access_token }
    );
    mutate({ show_onboarding: false, completed_at: null, skipped_at: response.skipped_at });
    return response;
  }, [session?.access_token, mutate]);

  const resetOnboarding = useCallback(async (): Promise<void> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    await apiClient.delete('/api/v1/me/onboarding', { token: session.access_token });
    mutate({ show_onboarding: true, completed_at: null, skipped_at: null });
  }, [session?.access_token, mutate]);

  const refresh = useCallback(() => {
    mutate();
  }, [mutate]);

  return {
    status: data,
    isLoading,
    error: error ?? null,
    showOnboarding: data?.show_onboarding ?? false,
    completeOnboarding,
    skipOnboarding,
    resetOnboarding,
    refresh,
  };
}
